import { type ProviderAuthenticationIntentResponse, ProviderAuthenticationCheckpointDetails } from '@unipile/types';
import { useTranslation } from 'react-i18next';
import { useProvider } from '../../../hooks/useProvider';
import { Alert, AlertTitle, AlertDescription } from '../../shadcn/Alert';
import { CaptchaCheckpoint } from './CaptchaCheckpoint';
import { ChallengeSelectionCheckpoint } from './ChallengeSelectionCheckpoint';
import { CodeCheckpoint } from './CodeCheckpoint';
import { ConsentScreenCheckpoint } from './ConsentScreenCheckpoint';
import { ContractSelectionCheckpoint } from './ContractSelectionCheckpoint';
import { EncryptedHistoryCheckpoint } from './EncryptedHistoryCheckpoint';
import { LoadingCheckpoint } from './LoadingCheckpoint';
import { OtpOrInAppValidationCheckpoint } from './OtpOrInAppValidationCheckpoint';
import { PhoneRegisterCheckpoint } from './PhoneRegisterCheckpoint';
import { ValidationCheckpoint } from './ValidationCheckpoint';

type Props = {
  checkpoint: ProviderAuthenticationCheckpointDetails;
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
};

/**
 * Translation keys for a code-entry description, most specific first.
 *
 * The generic wording ("sent to your phone number or email address") is wrong whenever the provider
 * told us which channel it actually used - Instagram routinely sends the code over WhatsApp. i18next
 * walks this list and takes the first key that resolves, so a provider or locale that has no variant
 * for the channel simply keeps the generic sentence instead of rendering a raw key.
 */
function channelDescriptionKeys(type: 'OTP' | 'OTP_OR_IN_APP_VALIDATION', channel?: 'EMAIL' | 'SMS' | 'WHATSAPP'): string[] {
  return channel ? [`${type}.${channel}.description`, `${type}.description`] : [`${type}.description`];
}

export function CheckpointScreen({ checkpoint, onSuccess, onError }: Props) {
  const { t } = useTranslation();
  const { provider } = useProvider();

  if (checkpoint.checkpoint.type === '2FA')
    return (
      <CodeCheckpoint
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
        length={6}
        title={t('2FA.title', { ns: provider })}
        description={t(`2FA.${checkpoint.checkpoint.platform}.description`, { ns: provider })}
        resendButton={provider === 'linkedin'}
      />
    );
  if (checkpoint.checkpoint.type === 'OTP')
    return (
      <CodeCheckpoint
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
        length={6}
        title={t('OTP.title', { ns: provider })}
        description={t(channelDescriptionKeys('OTP', checkpoint.checkpoint.channel), { ns: provider })}
        // Instagram falls back to a bare OTP checkpoint whenever in-app validation is not eligible
        // for the session - which is exactly when switching channel matters most, since the user is
        // otherwise stuck on whichever one Instagram picked.
        tryAnotherWayButton={provider === 'instagram'}
      />
    );
  if (checkpoint.checkpoint.type === 'OTP_OR_IN_APP_VALIDATION')
    return (
      <OtpOrInAppValidationCheckpoint
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
        length={6}
        title={t('OTP_OR_IN_APP_VALIDATION.title', { ns: provider })}
        description={t(channelDescriptionKeys('OTP_OR_IN_APP_VALIDATION', checkpoint.checkpoint.channel), { ns: provider })}
        tryAnotherWayButton={provider === 'instagram'}
      />
    );
  if (checkpoint.checkpoint.type === 'IN_APP_VALIDATION')
    return (
      <ValidationCheckpoint
        intent_id={checkpoint.intent_id}
        title={t('IN_APP_VALIDATION.title', { ns: provider })}
        description={t(`IN_APP_VALIDATION.description`, { ns: provider })}
        matchNumber={checkpoint.checkpoint.code}
        onSuccess={onSuccess}
        onError={onError}
        // Instagram: the approval screen (/auth_platform/afad) offers the same picker as the code entry one.
        tryAnotherWayButton={provider === 'linkedin' || provider === 'instagram'}
        resendButton={provider === 'linkedin'}
      />
    );

  if (checkpoint.checkpoint.type === 'PHONE_REGISTER')
    return (
      <PhoneRegisterCheckpoint
        intent_id={checkpoint.intent_id}
        title={t('PHONE_REGISTER.title', { ns: provider })}
        description={t(`PHONE_REGISTER.description`, { ns: provider })}
        onSuccess={onSuccess}
        onError={onError}
      />
    );

  if (checkpoint.checkpoint.type === 'CAPTCHA')
    return (
      <CaptchaCheckpoint
        checkpoint={checkpoint.checkpoint}
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
      />
    );

  if (checkpoint.checkpoint.type === 'CONSENT_SCREEN')
    return (
      <ConsentScreenCheckpoint
        checkpoint={checkpoint.checkpoint}
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
      />
    );

  if (checkpoint.checkpoint.type === 'CONTRACT_SELECTION')
    return (
      <ContractSelectionCheckpoint
        checkpoint={checkpoint.checkpoint}
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
      />
    );

  if (checkpoint.checkpoint.type === 'ENCRYPTED_HISTORY')
    return (
      <EncryptedHistoryCheckpoint
        checkpoint={checkpoint.checkpoint}
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
      />
    );

  if (checkpoint.checkpoint.type === 'CHALLENGE_SELECTION')
    return (
      <ChallengeSelectionCheckpoint
        checkpoint={checkpoint.checkpoint}
        intent_id={checkpoint.intent_id}
        onSuccess={onSuccess}
        onError={onError}
      />
    );

  if (checkpoint.checkpoint.type === 'LOADING')
    return (
      <LoadingCheckpoint
        intent_id={checkpoint.intent_id}
        title={t('LOADING.title', { ns: provider })}
        description={t('LOADING.description', { ns: provider })}
        onSuccess={onSuccess}
        onError={onError}
      />
    );

  return (
    <Alert>
      <AlertTitle>{t('Checkpoint.unsupported')}</AlertTitle>
      <AlertDescription>{t('Checkpoint.unsupportedDescription')}</AlertDescription>
    </Alert>
  );
}
